import React, { useState, useMemo, useCallback, useEffect } from 'react';
import {
    Autocomplete,
    Box,
    Tabs, Tab,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InputAdornment from '@mui/material/InputAdornment';
import GlCateDetail, {type GlCateCounterpart, type GlCateDetailRow} from '@/components/account/gl-invoice/mode/GlCateDetail';
import {
    GlColumnDef,
    GlSheetRowVO,
    buildColumnKey,
    isBalanceSheetFsCode,
    sumByFsCode,
} from '@/services/accountService/glInvoiceService';
import { resolveGlCurrentPeriodDisplay } from '@/utils/glToFsMapper';
import {
    buildExcludedBankSummaryKeys,
    isBankCashColumn,
    isExRateColumn,
} from '@/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import { isPpeColumn } from '@/components/account/gl-invoice/mode/tabs/ppeColumnUtils';
import { GlBankCashDetail, type GlBankTransferPick } from '@/components/account/gl-invoice/mode/tabs/GlBankCashDetail';
import { GlPpeDetail } from '@/components/account/gl-invoice/mode/tabs/GlPpeDetail';
import GlInvoiceTransferDrawer, {
    buildTransferDestinations,
} from '@/components/account/gl-invoice/mode/GlInvoiceTransferDrawer';
import { useGlInvoiceTransferDrawer, type GlTransferInvoicePayload } from '@/components/account/gl-invoice/mode/useGlInvoiceTransferDrawer';
import { OverlayMask } from '@/components/common/OverlayMask';
import {
    fetchInvoiceTransferHistory,
    revertGlInvoiceTransfer,
} from '@/services/accountService/glInvoiceService';
import { columnHeaderLabelWithAlias } from '@/pages/components/account/gl-invoice/columnAliasDisplay';

interface GlTinyColumnProps {
    companyId?: number;
    annualId?: number;
    columnDefs: GlColumnDef[];
    rows: GlSheetRowVO[];
    summaryCurrentPeriod: Record<string, number>;
    summaryPriorYear: Record<string, number>;
    onTransferSuccess?: () => void | Promise<void>;
    /** FS Summary 点击科目后定位到对应 Tiny tab */
    focusFsCode?: string | null;
    onFocusFsCodeConsumed?: () => void;
    /** Tab / 标题是否显示 Co.GL 客户别名 */
    showClientAlias?: boolean;
}

/** Tab 定义：普通列 Tab，或聚合 Bank / PPE Tab */
type TabDef =
    | { type: 'column'; key: string; label: string; columnKey: string; fsCode: string; nameZh: string; isMultiCol: boolean }
    | { type: 'bank'; key: string; label: string }
    | { type: 'ppe'; key: string; label: string };

/** 将 FS 科目码映射到 Tiny Mode tab key（bank / ppe / 列 tab） */
export function resolveTinyTabKeyForFsCode(fsCode: string, tabs: TabDef[]): string | null {
    const code = (fsCode || '').trim().toUpperCase();
    if (!code) return null;
    if (code === 'BS-CA-CASH') {
        return tabs.find((t) => t.type === 'bank')?.key ?? null;
    }
    if (code === 'BS-NCA-PPE') {
        return tabs.find((t) => t.type === 'ppe')?.key ?? null;
    }
    return tabs.find((t) => t.type === 'column' && t.fsCode.toUpperCase() === code)?.key ?? null;
}

/** FS Summary 可点击跳转 Tiny 的科目集合 */
export function collectGlTinyNavigableFsCodes(columnDefs: GlColumnDef[]): Set<string> {
    const set = new Set<string>();
    for (const d of columnDefs) {
        if (isExRateColumn(d)) continue;
        if (isBankCashColumn(d)) {
            set.add('BS-CA-CASH');
            continue;
        }
        if (isPpeColumn(d)) {
            set.add('BS-NCA-PPE');
            continue;
        }
        if (d.fsCode) set.add(d.fsCode);
    }
    return set;
}

/** Related Entries：同一发票上除当前 tab 外的非零科目；银行只保留 HKD/Ex HKD，不含原币与汇率列 */
function buildCounterpartsForTinyTab(
    columnValues: Record<string, number> | undefined,
    tab: Extract<TabDef, { type: 'column' }>,
    columnDefs: GlColumnDef[],
    showClientAlias = false,
): GlCateCounterpart[] {
    const excludedBankKeys = buildExcludedBankSummaryKeys(columnDefs);
    const counterparts: GlCateCounterpart[] = [];
    for (const [k, raw] of Object.entries(columnValues || {})) {
        const v = Number(raw) || 0;
        if (v === 0) continue;
        if (tab.isMultiCol) {
            if (k === tab.columnKey) continue;
        } else if (k === tab.fsCode || k.startsWith(`${tab.fsCode}|`)) {
            continue;
        }
        if (excludedBankKeys.has(k)) continue;
        const def = columnDefs.find((d) => buildColumnKey(d) === k);
        if (def && isExRateColumn(def)) continue;
        const title = def
            ? columnHeaderLabelWithAlias(def, showClientAlias)
            : (k.includes('|') ? k.slice(0, k.indexOf('|')) : k);
        counterparts.push({
            fsCode: def?.fsCode || (k.includes('|') ? k.slice(0, k.indexOf('|')) : k),
            fsName: title || def?.fsNameEn || def?.fsCode || k,
            columnTitle: title,
            amount: v,
            currency: def?.currency || 'HKD',
        });
    }
    counterparts.sort(
        (a, b) => Math.abs(Number(b.amount) || 0) - Math.abs(Number(a.amount) || 0),
    );
    return counterparts;
}

export default function GlTinyColumn(props: GlTinyColumnProps) {
    const {
        companyId,
        annualId,
        columnDefs,
        rows,
        summaryCurrentPeriod,
        summaryPriorYear,
        onTransferSuccess,
        showClientAlias = false,
        focusFsCode,
        onFocusFsCodeConsumed,
    } = props;

    const [activeTab, setActiveTab] = useState(0);
    const [selectionEpoch, setSelectionEpoch] = useState(0);
    const [searchOpen, setSearchOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    const transferDrawer = useGlInvoiceTransferDrawer(companyId, annualId);
    const {
        open: transferOpen,
        loading: transferHistoryLoading,
        history: transferHistory,
        invoices: transferInvoices,
        handleOpen: openTransfer,
        handleOpenFromSheetRow: openTransferFromSheetRow,
        handleClose: closeTransfer,
        refreshHistory: refreshTransferHistory,
    } = transferDrawer;
    const destinations = useMemo(() => buildTransferDestinations(columnDefs), [columnDefs]);

    // 計算哪些 fsCode 在非 bank/PPE columnDefs 中出現多次
    const multiColFsCodes = useMemo(() => {
        const count: Record<string, number> = {};
        for (const d of columnDefs) {
            if (isBankCashColumn(d) || isPpeColumn(d) || isExRateColumn(d)) continue;
            count[d.fsCode] = (count[d.fsCode] || 0) + 1;
        }
        const result = new Set<string>();
        for (const [code, n] of Object.entries(count)) {
            if (n > 1) result.add(code);
        }
        return result;
    }, [columnDefs]);

    // 明細行是否有銀行/現金列非零（匯總為 0 時仍應顯示 Bank tab）
    const hasBankRowActivity = useMemo(() => {
        return rows.some(row => sumByFsCode(row.columnValues, 'BS-CA-CASH') !== 0);
    }, [rows]);

    // 明細行是否有 PPE 列非零
    const hasPpeRowActivity = useMemo(() => {
        return rows.some(row => sumByFsCode(row.columnValues, 'BS-NCA-PPE') !== 0);
    }, [rows]);

    // 構建 Tab 列表：bank / PPE 列各聚合為 1 個 Tab，其餘各自 1 個 Tab
    const activeTabs = useMemo(() => {
        const tabs: TabDef[] = [];
        let hasBankData = false;
        let hasPpeData = false;

        for (const def of columnDefs) {
            if (isExRateColumn(def)) continue;

            // bank 列：檢查匯總是否有數據，不單獨建 Tab
            if (isBankCashColumn(def)) {
                if (!hasBankData) {
                    const colKey = buildColumnKey(def);
                    if ((summaryCurrentPeriod[colKey] ?? 0) !== 0
                        || (summaryPriorYear[colKey] ?? 0) !== 0) {
                        hasBankData = true;
                    }
                }
                continue;
            }

            // PPE 列：同樣聚合，不單獨建 Tab
            if (isPpeColumn(def)) {
                if (!hasPpeData) {
                    const colKey = buildColumnKey(def);
                    if ((summaryCurrentPeriod[colKey] ?? 0) !== 0
                        || (summaryPriorYear[colKey] ?? 0) !== 0) {
                        hasPpeData = true;
                    }
                }
                continue;
            }

            const isMulti = multiColFsCodes.has(def.fsCode);
            const colKey = buildColumnKey(def);

            if (isMulti) {
                // 多列 fsCode：每個子類別獨立 Tab，精確 key 匹配（當期或上年有值即顯示）
                const curVal = summaryCurrentPeriod[colKey] ?? 0;
                const priorVal = summaryPriorYear[colKey] ?? 0;
                if (curVal !== 0 || priorVal !== 0) {
                    tabs.push({
                        type: 'column',
                        key: `${def.fsCode}-${def.glCode}`,
                        label: columnHeaderLabelWithAlias(def, showClientAlias),
                        columnKey: colKey,
                        fsCode: def.fsCode,
                        nameZh: def.nameZh,
                        isMultiCol: true,
                    });
                }
            } else {
                // 單列 fsCode：按 fsCode 前綴匹配（當期或上年有值即顯示）
                const curTotal = sumByFsCode(summaryCurrentPeriod, def.fsCode);
                const priorTotal = sumByFsCode(summaryPriorYear, def.fsCode);
                if (curTotal !== 0 || priorTotal !== 0) {
                    tabs.push({
                        type: 'column',
                        key: `${def.fsCode}-${def.glCode}`,
                        label: columnHeaderLabelWithAlias(def, showClientAlias),
                        columnKey: colKey,
                        fsCode: def.fsCode,
                        nameZh: def.nameZh,
                        isMultiCol: false,
                    });
                }
            }
        }

        // 匯總全掃描 + 明細行兜底（銀行流水可能相互抵消但仍有明細）
        if (!hasBankData) {
            for (const def of columnDefs) {
                if (!isBankCashColumn(def) || isExRateColumn(def)) continue;
                const colKey = buildColumnKey(def);
                if ((summaryCurrentPeriod[colKey] ?? 0) !== 0
                    || (summaryPriorYear[colKey] ?? 0) !== 0) {
                    hasBankData = true;
                    break;
                }
            }
        }
        if (!hasBankData && hasBankRowActivity) {
            hasBankData = true;
        }

        if (!hasPpeData) {
            for (const def of columnDefs) {
                if (!isPpeColumn(def)) continue;
                const colKey = buildColumnKey(def);
                if ((summaryCurrentPeriod[colKey] ?? 0) !== 0
                    || (summaryPriorYear[colKey] ?? 0) !== 0) {
                    hasPpeData = true;
                    break;
                }
            }
        }
        if (!hasPpeData && hasPpeRowActivity) {
            hasPpeData = true;
        }

        // 順序：Bank → PPE → 其餘（與 Excel 列常見順序一致）
        if (hasPpeData) {
            tabs.unshift({ type: 'ppe', key: 'ppe', label: 'Property, plant and equipment' });
        }
        if (hasBankData) {
            tabs.unshift({ type: 'bank', key: 'bank-cash', label: 'Bank Balance and Cash' });
        }

        return tabs;
    }, [columnDefs, summaryCurrentPeriod, summaryPriorYear, multiColFsCodes, hasBankRowActivity, hasPpeRowActivity, showClientAlias]);

    // FS Summary → Tiny：按 fsCode 定位 tab
    useEffect(() => {
        if (!focusFsCode || activeTabs.length === 0) return;
        const key = resolveTinyTabKeyForFsCode(focusFsCode, activeTabs);
        if (!key) {
            onFocusFsCodeConsumed?.();
            return;
        }
        const idx = activeTabs.findIndex((t) => t.key === key);
        if (idx >= 0) {
            setActiveTab(idx);
            setSelectionEpoch((e) => e + 1);
        }
        onFocusFsCodeConsumed?.();
    }, [focusFsCode, activeTabs, onFocusFsCodeConsumed]);

    // 當前選中的 Tab
    const currentTab = activeTabs[Math.min(activeTab, activeTabs.length - 1)] ?? null;

    const handleCateRowClick = useCallback((row: GlCateDetailRow, amount: number) => {
        if (!currentTab || currentTab.type !== 'column') return;
        const parts = currentTab.columnKey.split('|');
        const glCode = parts.length > 1 ? parts.slice(1).join('|') : '';
        void openTransfer({
            invoiceId: row.invoiceId,
            date: row.date,
            description: row.description,
            amount,
            columnValues: row.columnValues,
            sourceFsCode: currentTab.fsCode,
            sourceGlCode: glCode || null,
            sourceLabel: currentTab.label,
        });
    }, [openTransfer, currentTab]);

    const handleCateTransferSelected = useCallback((
        picks: Array<{row: GlCateDetailRow; amount: number}>,
    ) => {
        if (!currentTab || currentTab.type !== 'column' || picks.length === 0) return;
        const parts = currentTab.columnKey.split('|');
        const glCode = parts.length > 1 ? parts.slice(1).join('|') : '';
        const payloads: GlTransferInvoicePayload[] = picks.map(({row, amount}) => ({
            invoiceId: row.invoiceId,
            date: row.date,
            description: row.description,
            amount,
            columnValues: row.columnValues,
            sourceFsCode: currentTab.fsCode,
            sourceGlCode: glCode || null,
            sourceLabel: currentTab.label,
        }));
        void openTransfer(payloads);
    }, [openTransfer, currentTab]);

    const handleBankRowClick = useCallback((
        row: GlSheetRowVO,
        amount: number,
        glCode?: string | null,
    ) => {
        void openTransferFromSheetRow(row, amount, {
            fsCode: 'BS-CA-CASH',
            glCode: glCode ?? undefined,
            label: 'Bank Balance and Cash',
        });
    }, [openTransferFromSheetRow]);

    const handleBankTransferSelected = useCallback((picks: GlBankTransferPick[]) => {
        if (picks.length === 0) return;
        const glCode = picks[0].glCode;
        const payloads: GlTransferInvoicePayload[] = picks.map((p) => ({
            invoiceId: p.row.invoiceId,
            date: p.row.date,
            description: p.row.description,
            amount: p.amount,
            columnValues: p.row.columnValues,
            sourceFsCode: 'BS-CA-CASH',
            sourceGlCode: glCode,
            sourceLabel: 'Bank Balance and Cash',
        }));
        void openTransfer(payloads);
    }, [openTransfer]);

    /** 勾选行原路返回：按每票最新 TINY_TRANSFER；同 batch 则整批，否则逐票 */
    const handleReturnSelectedInvoices = useCallback(async (invoiceIds: string[]) => {
        if (companyId == null || annualId == null || invoiceIds.length === 0) return;
        const uniqueIds = [...new Set(invoiceIds.filter(Boolean))];
        try {
            const history = await fetchInvoiceTransferHistory(companyId, annualId);
            const forward = (history || []).filter(
                (h) => !h.operationSource || h.operationSource === 'TINY_TRANSFER',
            );
            // history 已时间倒序；每票取第一条
            const latestByInvoice = new Map<string, { batchId: number; invoiceId: string }>();
            for (const h of forward) {
                if (!uniqueIds.includes(h.invoiceId)) continue;
                if (latestByInvoice.has(h.invoiceId)) continue;
                latestByInvoice.set(h.invoiceId, { batchId: h.batchId, invoiceId: h.invoiceId });
            }
            if (latestByInvoice.size === 0) {
                window.alert('No transfer history found for the selected invoices.');
                return;
            }
            const skipped = uniqueIds.filter((id) => !latestByInvoice.has(id));
            const targets = [...latestByInvoice.values()];
            const batchIds = [...new Set(targets.map((t) => t.batchId))];
            if (batchIds.length === 1) {
                const batchId = batchIds[0];
                const invoicesInBatch = new Set(
                    forward.filter((h) => h.batchId === batchId).map((h) => h.invoiceId),
                );
                const coversWholeBatch = invoicesInBatch.size === targets.length
                    && targets.every((t) => invoicesInBatch.has(t.invoiceId));
                if (coversWholeBatch) {
                    await revertGlInvoiceTransfer({
                        companyId,
                        annualId,
                        batchId,
                        invoiceId: null,
                    });
                } else {
                    for (const t of targets) {
                        await revertGlInvoiceTransfer({
                            companyId,
                            annualId,
                            batchId: t.batchId,
                            invoiceId: t.invoiceId,
                        });
                    }
                }
            } else {
                for (const t of targets) {
                    await revertGlInvoiceTransfer({
                        companyId,
                        annualId,
                        batchId: t.batchId,
                        invoiceId: t.invoiceId,
                    });
                }
            }
            setSelectionEpoch((n) => n + 1);
            await onTransferSuccess?.();
            if (skipped.length > 0) {
                window.alert(
                    `Returned ${targets.length} invoice(s). Skipped ${skipped.length} without transfer history.`,
                );
            }
        } catch (e: unknown) {
            window.alert(e instanceof Error ? e.message : 'Return failed');
        }
    }, [annualId, companyId, onTransferSuccess]);

    const handleCateReturnSelected = useCallback((
        picks: Array<{row: GlCateDetailRow; amount: number}>,
    ) => {
        void handleReturnSelectedInvoices(picks.map((p) => p.row.invoiceId));
    }, [handleReturnSelectedInvoices]);

    const handleBankReturnSelected = useCallback((picks: GlBankTransferPick[]) => {
        void handleReturnSelectedInvoices(picks.map((p) => p.row.invoiceId));
    }, [handleReturnSelectedInvoices]);

    const handleSheetRowClick = useCallback((row: GlSheetRowVO, amount: number) => {
        // PPE 按 BS-NCA-PPE 整池部分转移
        void openTransferFromSheetRow(row, amount, {
            fsCode: currentTab?.type === 'ppe' ? 'BS-NCA-PPE' : undefined,
            label: currentTab?.type === 'ppe' ? 'PPE' : undefined,
        });
    }, [openTransferFromSheetRow, currentTab]);

    // 從 rows 中篩選該列有值的行
    const filteredRows = useMemo(() => {
        if (!currentTab || currentTab.type !== 'column') return [];
        if (currentTab.isMultiCol) {
            // 多列 fsCode：精確 key 匹配
            return rows.filter(row => {
                const val = row.columnValues?.[currentTab.columnKey];
                return val != null && val !== 0;
            });
        }
        // 單列 fsCode：按 fsCode 前綴匹配
        return rows.filter(row => {
            const val = sumByFsCode(row.columnValues, currentTab.fsCode);
            return val !== 0;
        });
    }, [rows, currentTab]);

    // 當前列的匯總值（當期，与 FS Summary / Submit 同源）
    const columnTotal = useMemo(() => {
        if (!currentTab || currentTab.type !== 'column') return 0;
        if (currentTab.isMultiCol) {
            return summaryCurrentPeriod[currentTab.columnKey] ?? 0;
        }
        return resolveGlCurrentPeriodDisplay(
            currentTab.fsCode, summaryCurrentPeriod, summaryPriorYear, columnDefs,
        );
    }, [currentTab, summaryCurrentPeriod, summaryPriorYear, columnDefs]);

    // 當前列的匯總值（上年）—— 無數據時返回 undefined，避免渲染空的 "Prior Year:" 標籤
    const priorYearTotal = useMemo((): number | undefined => {
        if (!currentTab || currentTab.type !== 'column') return undefined;
        let val: number;
        if (currentTab.isMultiCol) {
            val = summaryPriorYear[currentTab.columnKey] ?? 0;
        } else {
            val = sumByFsCode(summaryPriorYear, currentTab.fsCode);
        }
        return val !== 0 ? val : undefined;
    }, [currentTab, summaryPriorYear]);

    // BS 科目：像銀行一樣在明細首行插入 Prior Year b/f（期末 = 上年 + 当期变动）
    const displayRows = useMemo((): GlCateDetailRow[] => {
        if (!currentTab || currentTab.type !== 'column') return [];
        const base: GlCateDetailRow[] = filteredRows.map((row) => ({
            invoiceId: row.invoiceId,
            date: row.date,
            description: row.description,
            voucherNo: row.voucherNo,
            invoiceNo: row.invoiceNo,
            details: row.details,
            columnValues: row.columnValues,
            counterparts: buildCounterpartsForTinyTab(
                row.columnValues, currentTab, columnDefs, showClientAlias,
            ),
        }));
        if (!isBalanceSheetFsCode(currentTab.fsCode)
            || priorYearTotal == null
            || priorYearTotal === 0) {
            return base;
        }
        const hasPrior = base.some((r) =>
            String(r.invoiceId || '').startsWith('__prior_bf__')
            || String(r.description || '').trim().startsWith('Prior Year b/f'),
        );
        if (hasPrior) return base;
        const priorRow: GlCateDetailRow = {
            invoiceId: `__prior_bf__:${currentTab.columnKey}`,
            date: null,
            description: 'Prior Year b/f +',
            voucherNo: null,
            invoiceNo: null,
            details: null,
            amount: priorYearTotal,
            columnValues: currentTab.isMultiCol
                ? {[currentTab.columnKey]: priorYearTotal}
                : undefined,
            counterparts: [],
        };
        return [priorRow, ...base];
    }, [currentTab, filteredRows, priorYearTotal, columnDefs, showClientAlias]);

    const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleSearchToggle = () => {
        setSearchOpen((open) => {
            if (open) setSearchQuery('');
            return !open;
        });
    };

    const searchProps = {
        searchOpen,
        searchQuery,
        onSearchToggle: handleSearchToggle,
        onSearchQueryChange: setSearchQuery,
    };

    /** 科目太多时：输入名称 / 中文 / fsCode 跳转到对应 tab */
    const tabJumpOptions = useMemo(() => {
        return activeTabs.map((tab, index) => {
            const secondary = tab.type === 'column'
                ? [tab.nameZh, tab.fsCode].filter((s) => (s || '').trim()).join(' · ')
                : tab.type === 'bank'
                    ? 'BS-CA-CASH'
                    : 'BS-NCA-PPE';
            return {
                index,
                key: tab.key,
                label: tab.label,
                secondary,
            };
        });
    }, [activeTabs]);

    if (activeTabs.length === 0) {
        return (
            <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                No columns with data
            </Box>
        );
    }

    const transferEnabled = companyId != null && annualId != null;

    return (
        <Box>
            {/* Tab 列表 + 跳转搜索 */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    borderBottom: 1,
                    borderColor: 'divider',
                    gap: 1,
                    pr: 1,
                }}
            >
                <Tabs
                    value={Math.min(activeTab, activeTabs.length - 1)}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ flex: 1, minWidth: 0, minHeight: 40 }}
                >
                    {activeTabs.map((tab) => (
                        <Tab
                            key={tab.key}
                            label={tab.label}
                            sx={{ textTransform: 'none', minHeight: 40, fontSize: 13 }}
                        />
                    ))}
                </Tabs>
                {tabJumpOptions.length > 6 && (
                    <Autocomplete
                        size="small"
                        sx={{ width: 240, flexShrink: 0, my: 0.5 }}
                        options={tabJumpOptions}
                        value={null}
                        blurOnSelect
                        clearOnBlur
                        handleHomeEndKeys
                        getOptionLabel={(o) => o.label}
                        isOptionEqualToValue={(a, b) => a.key === b.key}
                        filterOptions={(options, state) => {
                            const q = state.inputValue.trim().toLowerCase();
                            if (!q) return options;
                            return options.filter((o) =>
                                o.label.toLowerCase().includes(q)
                                || o.secondary.toLowerCase().includes(q)
                                || o.key.toLowerCase().includes(q),
                            );
                        }}
                        onChange={(_, opt) => {
                            if (opt) setActiveTab(opt.index);
                        }}
                        renderOption={(props, option) => (
                            <li {...props} key={option.key}>
                                <Box sx={{py: 0.25}}>
                                    <Typography variant="body2" sx={{lineHeight: 1.3}}>
                                        {option.label}
                                    </Typography>
                                    {option.secondary ? (
                                        <Typography variant="caption" color="text.secondary" sx={{lineHeight: 1.2}}>
                                            {option.secondary}
                                        </Typography>
                                    ) : null}
                                </Box>
                            </li>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Go to account"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon fontSize="small" color="action"/>
                                        </InputAdornment>
                                    ),
                                }}
                            />
                        )}
                    />
                )}
            </Box>

            {/* Tab 內容 */}
            {currentTab && currentTab.type === 'bank' && (
                <GlBankCashDetail
                    key={`bank-${selectionEpoch}`}
                    columnDefs={columnDefs}
                    rows={rows}
                    summaryCurrentPeriod={summaryCurrentPeriod}
                    summaryPriorYear={summaryPriorYear}
                    onRowClick={transferEnabled ? handleBankRowClick : undefined}
                    transferSelectable={transferEnabled}
                    onTransferSelected={transferEnabled ? handleBankTransferSelected : undefined}
                    onReturnSelected={transferEnabled ? handleBankReturnSelected : undefined}
                    {...searchProps}
                />
            )}
            {currentTab && currentTab.type === 'ppe' && (
                <GlPpeDetail
                    columnDefs={columnDefs}
                    rows={rows}
                    summaryCurrentPeriod={summaryCurrentPeriod}
                    summaryPriorYear={summaryPriorYear}
                    onRowClick={transferEnabled ? handleSheetRowClick : undefined}
                    {...searchProps}
                />
            )}
            {currentTab && currentTab.type === 'column' && (
                <GlCateDetail
                    key={`cate-${currentTab.columnKey}-${selectionEpoch}`}
                    label={currentTab.label}
                    nameZh={currentTab.nameZh}
                    fsCode={currentTab.fsCode}
                    columnTotal={columnTotal}
                    priorYearTotal={priorYearTotal}
                    rows={displayRows}
                    columnKey={currentTab.columnKey}
                    isMultiCol={currentTab.isMultiCol}
                    onRowClick={transferEnabled ? handleCateRowClick : undefined}
                    transferSelectable={transferEnabled}
                    onTransferSelected={transferEnabled ? handleCateTransferSelected : undefined}
                    onReturnSelected={transferEnabled ? handleCateReturnSelected : undefined}
                    {...searchProps}
                />
            )}

            {transferEnabled && (
                <>
                    <OverlayMask open={transferOpen} clickable onClick={closeTransfer} />
                    <GlInvoiceTransferDrawer
                        open={transferOpen}
                        onClose={closeTransfer}
                        companyId={companyId!}
                        annualId={annualId!}
                        invoices={transferInvoices}
                        destinations={destinations}
                        history={transferHistory}
                        historyLoading={transferHistoryLoading}
                        onHistoryRefresh={refreshTransferHistory}
                        onTransferSuccess={async () => {
                            setSelectionEpoch((n) => n + 1);
                            await onTransferSuccess?.();
                        }}
                    />
                </>
            )}
        </Box>
    );
}
